import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { ApiError } from '../../lib/api';
import { updateAccount } from '../accounts';
import { useCurrentUser } from '../auth';
import type { CoverageTier, ReportingPullMode } from '../../types/reporting';
import { getReportingCoverage, listBackfills, triggerBackfill } from './api';
import {
  accountRunningBackfill,
  anyBackfillRunning,
  pullModeLabel,
} from './backfillView';
import { formatCoverageRange, tierGapNote, tierGapTooltip } from './coverageView';

// Analytics Coverage — the per-account reporting home on the Meta Setup page
// (Phase 3 of the reporting ops-UI plan). Scoped to the selected account:
//   - the three tiers' coverage (campaign / ad-structural / demographics) with
//     gap notes, from the coverage endpoint filtered to this account;
//   - the recurring daily-sync toggle (Build A's reportingSyncEnabled flag);
//   - trigger buttons for the four tiered pulls (Phase 1), non-blocking Cloud Run
//     Job executions, with in-flight status when one is running.
// ADMIN-ONLY (an ops surface — the coverage + trigger endpoints are admin-gated),
// so the whole section is gated + the tab is hidden for non-admins.

const TIERS: { key: 'campaign' | 'adStructural' | 'demographics'; label: string; hint: string }[] = [
  { key: 'campaign', label: 'Campaign', hint: 'Topline daily coverage (every synced account).' },
  { key: 'adStructural', label: 'Ad-structural', hint: 'Ad/creative drill coverage (ad-grain pulls).' },
  { key: 'demographics', label: 'Demographics', hint: 'Per-ad demographic coverage.' },
];

const PULL_MODES: { mode: ReportingPullMode; label: string; hint: string }[] = [
  { mode: 'structural-recent', label: 'Structural · recent', hint: 'Last 90 days of ad/creative structure' },
  { mode: 'structural-full', label: 'Structural · full', hint: 'Full 25-month ad/creative backfill' },
  { mode: 'demographics-recent', label: 'Demographics · recent', hint: 'Last 90 days of per-ad demographics' },
  { mode: 'demographics-full', label: 'Demographics · full', hint: 'Daily-grain demographics for the 13 months the report covers' },
];

function Card({ children }: { children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-5 py-4">
        <h2 className="text-base font-semibold text-slate-900">Analytics Coverage</h2>
        <p className="mt-0.5 text-sm text-slate-500">
          Reporting data this account has, plus the controls to fill it — turn daily sync on and pull
          the tiers. Pulls run in the background (watch progress on the Backfills page).
        </p>
      </div>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

function TierRow({ label, hint, tier }: { label: string; hint: string; tier: CoverageTier | null }) {
  const note = tierGapNote(tier);
  return (
    <div className="flex items-baseline justify-between gap-4 py-2">
      <div>
        <div className="text-sm font-medium text-slate-800">{label}</div>
        <div className="text-xs text-slate-500">{hint}</div>
      </div>
      <div className="inline-flex items-center gap-1.5 whitespace-nowrap tabular-nums text-sm text-slate-700">
        {tier ? formatCoverageRange(tier) : <span className="text-slate-300">— no data yet</span>}
        {note && (
          <span
            className="text-[11px] font-normal text-slate-400"
            title={tierGapTooltip(tier) ?? undefined}
          >
            {note}
          </span>
        )}
      </div>
    </div>
  );
}

export function AnalyticsCoverageSection({
  accountId,
  accountName,
  initialSyncEnabled,
}: {
  accountId: string;
  accountName: string;
  initialSyncEnabled: boolean;
}) {
  const me = useCurrentUser();
  const qc = useQueryClient();
  const hasAccount = accountId.trim().length > 0;
  const isAdmin = me.role === 'admin';

  // Local mirror of the sync flag so the toggle reflects a click immediately (the
  // eligible-accounts source is server-cached ~60s). Reseeds on account change.
  const [syncOn, setSyncOn] = useState(initialSyncEnabled);
  useEffect(() => {
    setSyncOn(initialSyncEnabled);
  }, [initialSyncEnabled, accountId]);

  const coverage = useQuery({
    queryKey: ['reportingCoverage', accountId],
    queryFn: () => getReportingCoverage(accountId),
    enabled: hasAccount && isAdmin,
  });

  // Shares the Backfills page's cache key; polls while any backfill runs so this
  // account's in-flight state clears the moment its run finishes.
  const backfills = useQuery({
    queryKey: ['reporting', 'backfills'],
    queryFn: listBackfills,
    enabled: isAdmin,
    refetchInterval: (q) => (anyBackfillRunning(q.state.data?.backfills ?? []) ? 10000 : false),
  });

  const running = accountRunningBackfill(backfills.data?.backfills ?? [], accountId);

  const toggleSync = useMutation({
    mutationFn: (next: boolean) => updateAccount(accountId, { reportingSyncEnabled: next }),
    onSuccess: (account) => {
      setSyncOn(account.reportingSyncEnabled ?? false);
      void qc.invalidateQueries({ queryKey: ['metaAdEligibleAccounts'] });
      toast.success(account.reportingSyncEnabled ? 'Daily sync on.' : 'Daily sync off.');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't update daily sync."),
  });

  const trigger = useMutation({
    mutationFn: (mode: ReportingPullMode) => triggerBackfill(accountId, { pullMode: mode }),
    onSuccess: (_res, mode) => {
      void qc.invalidateQueries({ queryKey: ['reporting', 'backfills'] });
      toast.success('Pull started', {
        description: `${pullModeLabel(mode)} for ${accountName}.`,
        action: { label: 'View', onClick: () => window.location.assign('/reporting/backfills') },
      });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't start the pull."),
  });

  if (!isAdmin) {
    return (
      <Card>
        <p className="text-sm text-slate-500">
          Analytics coverage is an admin-only ops view. Ask an admin to manage this account's
          reporting pulls.
        </p>
      </Card>
    );
  }

  if (!hasAccount) {
    return (
      <Card>
        <p className="text-sm text-slate-500">Select an account above to view its analytics coverage.</p>
      </Card>
    );
  }

  const row = coverage.data?.accounts[0] ?? null;
  // An account may already have a run in flight (started here or elsewhere); block
  // a second concurrent trigger for it while so.
  const busy = running !== null || trigger.isPending;

  return (
    <Card>
      {/* Daily-sync toggle (Build A). */}
      <div className="flex items-center justify-between gap-4 pb-4">
        <div>
          <div className="text-sm font-medium text-slate-800">Recurring daily sync</div>
          <div className="text-xs text-slate-500">
            Includes this account in the 2-hourly recurring pull that keeps reporting fresh.
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={syncOn}
          aria-label="Recurring daily sync"
          disabled={toggleSync.isPending}
          onClick={() => toggleSync.mutate(!syncOn)}
          className={[
            'inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50',
            syncOn ? 'bg-kyck-600' : 'bg-slate-300',
          ].join(' ')}
        >
          <span
            className={[
              'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
              syncOn ? 'translate-x-5' : 'translate-x-0.5',
            ].join(' ')}
          />
        </button>
      </div>

      {/* Coverage tiers. */}
      <div className="border-t border-slate-100 pt-2">
        {coverage.isLoading ? (
          <CenteredSpinner />
        ) : coverage.error ? (
          <p className="py-2 text-sm text-red-600">
            {coverage.error instanceof ApiError ? coverage.error.message : "Couldn't load coverage."}
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {TIERS.map((t) => (
              <TierRow key={t.key} label={t.label} hint={t.hint} tier={row?.[t.key] ?? null} />
            ))}
          </div>
        )}
      </div>

      {/* Tiered pull triggers. */}
      <div className="mt-4 border-t border-slate-100 pt-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Pull data</h3>
          {running && (
            <span className="inline-flex items-center gap-2 text-sm text-amber-700">
              <span className="h-2 w-2 animate-pulse rounded-full bg-amber-500" />
              {pullModeLabel(running.pullMode)} running…
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {PULL_MODES.map((m) => (
            <Button
              key={m.mode}
              variant="outline"
              size="sm"
              disabled={busy}
              title={m.hint}
              onClick={() => trigger.mutate(m.mode)}
              className="h-auto justify-start whitespace-normal py-2 text-left"
            >
              <span className="flex flex-col items-start">
                <span className="font-medium">{m.label}</span>
                <span className="text-xs font-normal text-slate-500">{m.hint}</span>
              </span>
            </Button>
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Runs a background Cloud Run job for this account — non-blocking, survives closing the tab.
          One pull at a time per account; watch progress on the{' '}
          <a href="/reporting/backfills" className="text-sky-700 underline hover:text-sky-900">
            Backfills page
          </a>
          .
        </p>
      </div>
    </Card>
  );
}
